import { WorkOrderData } from '../types';
import { toBankDossier } from './adapter';
import { BankDossier, BankDossierResponse } from './types';

/**
 * Dossiers of a list page by work order id. An id that was asked for and is
 * missing from the batch response maps to `null` (the OS has no dossier); an
 * id that was not asked for yet is absent, so its row keeps loading.
 */
export type BankDossierLookup = Map<number, BankDossier | null>;

export const buildDossierLookup = (
  ids: number[],
  responses: BankDossierResponse[]
): BankDossierLookup => {
  const lookup: BankDossierLookup = new Map(ids.map((id) => [id, null]));
  responses.forEach((response) =>
    lookup.set(response.work_order_id, toBankDossier(response))
  );
  return lookup;
};

export const mergeDossierLookups = (
  lookups: Array<BankDossierLookup | undefined>
): BankDossierLookup | undefined => {
  const present = lookups.filter(
    (lookup): lookup is BankDossierLookup => !!lookup
  );
  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0];
  return new Map(present.flatMap((lookup) => Array.from(lookup.entries())));
};

/** Fills `bank_dossier` on the rows the lookup knows; same array when none. */
export const mergeBankDossiers = (
  rows: WorkOrderData[],
  lookup: BankDossierLookup | undefined
): WorkOrderData[] => {
  if (!lookup || lookup.size === 0) return rows;
  let changed = false;
  const merged = rows.map((row) => {
    if (!lookup.has(row.id)) return row;
    const dossier = lookup.get(row.id) ?? null;
    if (row.bank_dossier === dossier) return row;
    changed = true;
    return { ...row, bank_dossier: dossier };
  });
  return changed ? merged : rows;
};
